import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Modal, IconButton } from "@mui/material";
import FilterBar from "@/Views/ImportJob/Dashboard/FilterBox";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import {
  LuPlus,
  LuRefreshCw,
  LuSettings,
  CgMoreO,
  IoMdSettings,
} from "@/Components/Icons/Icons.jsx";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";
import schema from "@/Config/ImportJob/Dashboard/Dashboard";
import CustomizeList from "@/Views/ImportJob/Dashboard/CustomizeList";
import { useNavigate } from "react-router-dom";
import GridExample from "@/Components/Tables/AgGridTable";
import initialColumns from "@/Config/ImportJob/Dashboard/TableColumnJson";
import { apiurl } from "@/Config/ApiConfig/ApiUrl";
import { useGetimportDashboardQuery } from "@/redux/ImportPage/Dashboard/DashboardSlice";
import { useUser } from "@/Context/UserContext";
import { useApplyimportDashboardFilterMutation } from "@/redux/ImportPage/Dashboard/DashboardfilterSlice";
import { useGlobalFilter } from "@/Context/GlobalFilterContext";

function Dashboard() {
  const navigate = useNavigate();
  const gridRef = useRef();
  const { clearAll, isDataFetched } = useUser();
  const { financialYear, city } = useGlobalFilter();

  const [currentPage, setCurrentPage] = useState(1); // 1-based
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // const { data, isLoading, isFetching, refetch } = useGetimportDashboardQuery();
  const { data, isLoading, refetch, isError, isFetching } =
    useGetimportDashboardQuery({
      page: currentPage,
      pageSize: rowsPerPage,
      financialYear,
      cityId: city?.id || "",
      // organizationId: user?.organizationId || "",
    });

  const tableData =
    isError || !data?.rows || data?.rows?.length === 0 ? [] : data.rows;
  const totalCount = data?.totalCount ?? 0;

  const [openCustomize, setOpenCustomize] = useState(false);
  const [columnDefs, setColumnDefs] = useState(initialColumns);
  const visibleColumns = columnDefs.filter((col) => col.checked);

  const [applyDashboardFilter] = useApplyimportDashboardFilterMutation();
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newloading, setNewLoading] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // ✅ Clear jobId on initial mount
  useEffect(() => {
    localStorage.removeItem("jobId");
    refetch();
  }, [refetch]);

  // ✅ Handle filter search
  const handleSearch = async (filterPayload) => {
    if (!filterPayload) {
      setFilteredData([]);
      setIsFilterApplied(false);
      return;
    }

    setLoading(true);

    try {
      const res = await applyDashboardFilter(filterPayload).unwrap();

      const filteredResult = res?.data?.data ?? [];

      setFilteredData(filteredResult);
      setIsFilterApplied(true);
    } catch (err) {
      setFilteredData([]);
      setIsFilterApplied(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilter = () => {
    setFilteredData([]);
    setIsFilterApplied(false);
  };

  const finalData =
    Array.isArray(filteredData) && isFilterApplied
      ? filteredData
      : Array.isArray(tableData)
        ? tableData
        : [];

        const handleCustomizeSave = (newColumns) => {
          // Update grid immediately
          setColumnDefs(newColumns);
          gridRef.current?.api?.setColumnDefs(newColumns);
        
          // 🧠 Check if all columns are selected
          const allSelected = newColumns.every((col) => col.checked);
        
          if (allSelected) {
            // 🔥 If all selected, remove saved data (reset to default)
            localStorage.removeItem("savedColumns");
          } else {
            // 💾 Otherwise, save customized layout
            localStorage.setItem("savedColumns", JSON.stringify(newColumns));
          }
          
          setOpenCustomize(false);
        };
        
        
  
    // on mount or tab return
  useEffect(() => {
    const saved = localStorage.getItem("savedColumns");
    if (saved) {
      const parsed = JSON.parse(saved);
      setColumnDefs(parsed);
      gridRef.current?.api?.setColumnDefs(parsed);
    }
  }, []);

  const handleCreateNewEntry = () => {
    setNewLoading(true);
    clearAll();
    navigate(apiurl.Importdashboard_CreateJob);
  };

  const handleRowClick = (rowData) => {
    if (rowData?.jobId) {
      localStorage.setItem("jobId", rowData.jobId);
    }
    navigate(`${apiurl.Importdashboard_CreateJob}/${rowData.jobId}`, {
      state: { rowData },
    });
  };

  // ✅ Add this below your API call section
  const [formValues, setFormValues] = useState({
    totalJobCreations: 0,
    totalImporters: 0,
    Tracking: 0,
    ActiveClients: 0,
    totalJobCreationsPercentage: 0,
    totalExportsPercentage: 0,
    trackingPercentage: 0,
    activeClientsPercentage: 0,
    totalJobCreationsArrow: "Up",
    totalExportsArrow: "Up",
    trackingArrow: "Up",
    activeClientsArrow: "Up",
  });

  // ✅ Whenever your API data changes, update these dynamically
  useEffect(() => {
    if (!data) return;

    setFormValues({
      totalJobCreations: data?.totalCount ?? 0,
      totalImporters: data?.totalCount ?? 0,
      Tracking: data?.TrackingCount ?? 0,
      ActiveClients: data?.TrackingCount ?? 0,

      totalJobCreationsPercentage: data?.lastWeekPercentage ?? 0,
      totalExportsPercentage: data?.lastMonthPercentage ?? 0,
      // trackingPercentage: data?.thisWeekPercentage ?? 0,
      // activeClientsPercentage: data?.thisMonthPercentage ?? 0,

      totalJobCreationsArrow: data?.weekComparison,
      totalExportsArrow: data?.monthComparison,
      // trackingArrow: data?.monthComparison,
      // activeClientsArrow: data?.weekComparison,
    });
  }, [data]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "var(--v-gap-sm)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="heading">Import Overview</Typography>
          <Typography variant="bodySmall">
            You can see all the Jobs here
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <BlackButton
            variant="bodySmallStrong"
            label= "Create New Entry"
            iconName={!newloading ? "LuPlus" : null}
            loading={newloading}
            loadingText="Create New Entry"
            onClick={handleCreateNewEntry}
            sx={{
              width: 180,
              color: "var(--gray-50)",
              backgroundColor: "var(--brand-600)",
              textTransform: "none",
              borderRadius: 2,
               "&:hover": { backgroundColor: "var(--brand-600)" },
            }}
          />
        </Box>
      </Box>

      {/* Filters and Form */}
      <FormGenerator
        schema={schema}
        showLoader={false}
        initialData={{
          totalJobCreations: {
            value: data?.totalCount ?? 0,
            percentage: data?.lastWeekPercentage ?? 0,
            arrowDirection: data?.weekComparison ?? "Up",
          },
          totalImporters: {
            value: data?.totalCount ?? 0,
            percentage: data?.lastMonthPercentage ?? 0,
            arrowDirection: data?.monthComparison ?? "Up",
          },
          Tracking: {
            value: data?.Tracking ?? 0,
            // percentage: data?.thisWeekPercentage ?? 0,
            // arrowDirection: data?.trackingComparison ?? "Up",
          },
          ActiveClients: {
            value: data?.Tracking ?? 0,
            // percentage: data?.thisMonthPercentage ?? 0,
            // arrowDirection: data?.activeClientomparison ?? "Up",
          },
        }}
      />
      <FilterBar
        onSearch={handleSearch}
        onReset={handleResetFilter}
        setFilteredData={setFilteredData}
        setIsFilterApplied={setIsFilterApplied}
      />

      {/* Table */}
      <Box
        sx={{ borderRadius: "10px", backgroundColor: "var(--gray-0)", mb: 2 }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          mt={2}
          px={2}
        >
          <Typography variant="subheading">Jobs</Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton sx={{ color: "var(--gray-500)" }} onClick={refetch}>
              <LuRefreshCw />
            </IconButton>

            <IconButton
              onClick={() => setOpenCustomize((prev) => !prev)}
              sx={{
                color: openCustomize ? "var(--brand-600)" : "var(--gray-500)",
                backgroundColor: openCustomize
                  ? "var(--brand-300)"
                  : "transparent",
                borderRadius: "50%",
                transition: "all 0.3s ease",
              }}
            >
              {openCustomize ? <IoMdSettings /> : <LuSettings />}
            </IconButton>

            <IconButton sx={{ color: "var(--gray-500)" }}>
              <CgMoreO />
            </IconButton>
          </Box>
        </Box>

        <GridExample
          key={visibleColumns.map((c) => c.field).join(",")}
          type="import"
          gridRef={gridRef}
          columns={visibleColumns}
          tableData={finalData}
          totalCount={totalCount}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          handleRowClick={handleRowClick}
          loading={loading || isLoading || isFetching}
          refetch={refetch}  // ✅ Pass refetch here

        />
      </Box>

      {/* Customize List Modal */}
      <Modal open={openCustomize} onClose={() => setOpenCustomize(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--gray-0)",
            boxShadow: 24,
            borderRadius: 2,
            p: 2,
          }}
        >
          <CustomizeList
            columns={columnDefs}
            onSave={handleCustomizeSave}
            onClose={() => setOpenCustomize(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
}

export default Dashboard;
